import { useState } from 'react';

export default function ApplicationScenarios() {
  const [activeScenario, setActiveScenario] = useState(0);

  const scenarios = [
    {
      title: 'Backyard Office',
      subtitle: 'Remote Work Studio',
      description: 'Perfect home office solution for remote professionals seeking dedicated workspace without commuting',
      image: '/images/ai-modern-apple-cabin-office-in-residential-4fc625.webp',
      icon: 'ri-home-office-line',
      useCases: ['Remote work professionals', 'Freelancers and consultants', 'Home-based businesses', 'Creative studios'],
      benefits: [
        { label: 'Setup Time', value: '2-3 days' },
        { label: 'ROI Period', value: '3-5 years' },
        { label: 'Value Add', value: '15-20%' },
        { label: 'Tax Benefit', value: 'Deductible' }
      ]
    },
    {
      title: 'Construction Site Office',
      subtitle: 'Project Management Hub',
      description: 'Temporary on-site office for construction projects, easily relocated as work progresses',
      image: '/images/ai-modular-office-cabin-at-construction-sit-365cc1.webp',
      icon: 'ri-building-line',
      useCases: ['Construction project offices', 'Site supervision centers', 'Contractor headquarters', 'Safety briefing rooms'],
      benefits: [
        { label: 'Deployment', value: '1-2 days' },
        { label: 'Mobility', value: 'Fully portable' },
        { label: 'Durability', value: '10+ years' },
        { label: 'Weather', value: 'All-season' }
      ]
    },
    {
      title: 'Sales Office',
      subtitle: 'Marketing Center',
      description: 'Attractive sales center for property developments, showrooms, and customer-facing operations',
      image: '/images/ai-modern-sales-office-cabin-with-professio-578a74.webp',
      icon: 'ri-store-line',
      useCases: ['Property sales centers', 'Product showrooms', 'Customer service hubs', 'Brand experience centers'],
      benefits: [
        { label: 'First Impression', value: 'Premium' },
        { label: 'Customisation', value: 'Full branding' },
        { label: 'Flexibility', value: 'Relocatable' },
        { label: 'Investment', value: 'Reusable' }
      ]
    },
    {
      title: 'Creative Park Workspace',
      subtitle: 'Business Park Office',
      description: 'Scalable office solution for business parks, creative hubs, and co-working developments',
      image: '/images/915e876bd1a3.webp',
      icon: 'ri-community-line',
      useCases: ['Business park offices', 'Co-working spaces', 'Startup incubators', 'Creative clusters'],
      benefits: [
        { label: 'Scalability', value: 'Modular' },
        { label: 'Cost', value: '40% less' },
        { label: 'Timeline', value: 'Weeks not months' },
        { label: 'Flexibility', value: 'Expandable' }
      ]
    },
    {
      title: 'Exhibition Hall',
      subtitle: 'Pop-up Commercial Space',
      description: 'Temporary or permanent exhibition space for trade shows, product launches, and retail pop-ups',
      image: '/images/e6379fec28de.webp',
      icon: 'ri-gallery-line',
      useCases: ['Trade show booths', 'Product launch venues', 'Pop-up retail stores', 'Art galleries'],
      benefits: [
        { label: 'Setup', value: 'Rapid' },
        { label: 'Layout', value: 'Customisable' },
        { label: 'Branding', value: 'Full control' },
        { label: 'Reuse', value: 'Multiple events' }
      ]
    },
    {
      title: 'Resort Office',
      subtitle: 'Hospitality Management',
      description: 'Administrative office for resorts, hotels, and hospitality venues blending with natural surroundings',
      image: '/images/ai-modern-office-cabin-at-resort-location-w-b26d32.webp',
      icon: 'ri-hotel-line',
      useCases: ['Resort management offices', 'Hotel administration', 'Guest services centers', 'Spa reception areas'],
      benefits: [
        { label: 'Aesthetics', value: 'Harmonious' },
        { label: 'Footprint', value: 'Minimal' },
        { label: 'Integration', value: 'Natural' },
        { label: 'Function', value: 'Full service' }
      ]
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-[#0A0A0A] w-full">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Application <span className="text-gray-300">Scenarios</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Versatile solutions for diverse business needs
          </p>
        </div>

        {/* Scenarios Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 w-full">
          {scenarios.map((scenario, index) => (
            <div 
              key={index}
              onMouseEnter={() => setActiveScenario(index)}
              className={`group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 cursor-pointer w-full ${
                activeScenario === index
                  ? 'border-gray-400 shadow-[0_0_40px_rgba(148,163,184,0.3)]'
                  : 'border-white/10 hover:border-gray-500/30'
              }`}
            >
              {/* Image */}
              <div className="relative w-full h-56 sm:h-64 md:h-72 overflow-hidden">
                <img 
                  src={scenario.image}
                  alt={scenario.title}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                
                {/* Icon Badge */}
                <div className="absolute top-4 sm:top-6 left-4 sm:left-6 w-12 h-12 sm:w-14 sm:h-14 bg-gray-500/20 backdrop-blur-xl rounded-xl sm:rounded-2xl flex items-center justify-center border border-white/20">
                  <i className={`${scenario.icon} text-xl sm:text-2xl text-gray-300`}></i>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-medium text-white mb-3 sm:mb-4 break-words">{scenario.title}</h3>
                <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 leading-relaxed break-words">{scenario.description}</p>

                {/* Use Cases */}
                <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 w-full">
                  {scenario.useCases.map((useCase, idx) => (
                    <div key={idx} className="flex items-start gap-2 sm:gap-3">
                      <i className="ri-arrow-right-s-line text-gray-400 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-xs sm:text-sm text-gray-300 break-words">{useCase}</span>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                  {scenario.benefits.map((benefit, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4 w-full">
                      <div className="text-xs text-gray-500 mb-1">{benefit.label}</div>
                      <div className="text-sm sm:text-base font-medium text-white break-words">{benefit.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}